'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFormInstance } from '@weareconceptstudio/form';
import { useTranslation, useUi } from '@weareconceptstudio/core';
import { AddressForm, useAccountContext, useAddressContext } from '@weareconceptstudio/account';
import {
	DEFAULT_ARMENIA_BBOX,
	fetchAddressSuggestions,
	formatAddressLabel,
	normalizeBranchText,
	formatBranchHours,
	resolveGeocodePrefill,
	resolveSuggestionPrefill,
} from '@/modules/Home/components/Hero/DeliveryBar/utils';

export const DELIVERY_SEARCH_FIELD = 'deliverySearch';
export const PENDING_ID = '__pending__';

export const useDeliveryTemplateSelector = ({
	globalData,
	isTakeaway,
	selectedAddressId,
	selectedBranchId,
	setSelectedAddressId,
	setSelectedBranchId,
	pendingAddress,
	hasAddressType,
	onBeforeAddressFormOpen,
}) => {
	const { openPopup } = useUi();
	const { selectedLang, translate } = useTranslation();
	const { fulfillment } = useAccountContext();
	const { addresses, addressLoading } = useAddressContext();
	const { watch, setValue } = useFormInstance() || {};

	const branches = fulfillment?.branches || [];

	const [showDropdown, setShowDropdown] = useState(false);
	const [isLocating, setIsLocating] = useState(false);
	const [addressSuggestions, setAddressSuggestions] = useState([]);
	const [isSuggestLoading, setIsSuggestLoading] = useState(false);

	const dropdownRef = useRef(null);
	const sessionTokenRef = useRef(null);
	const debounceTimeoutRef = useRef(null);
	const suggestAbortRef = useRef(null);
	const selectedLangRef = useRef(selectedLang);
	selectedLangRef.current = selectedLang;

	const searchQuery = watch?.(DELIVERY_SEARCH_FIELD) || '';
	const isSearching = searchQuery.trim().length > 0;

	const addressList = useMemo(() => {
		if (hasAddressType) return addresses?.shipping || [];
		return Array.isArray(addresses) ? addresses : [];
	}, [hasAddressType, addresses]);

	const selectedAddress = useMemo(
		() => addressList.find((a) => a.id === selectedAddressId),
		[addressList, selectedAddressId]
	);

	const selectedBranch = useMemo(
		() => branches.find((b) => Number(b.id) === Number(selectedBranchId)),
		[branches, selectedBranchId]
	);

	const syncSearchFieldValue = useCallback(
		(value = '') => {
			setValue?.(DELIVERY_SEARCH_FIELD, value);
		},
		[setValue]
	);

	const getSessionToken = useCallback(() => {
		if (!sessionTokenRef.current) sessionTokenRef.current = uuidv4();
		return sessionTokenRef.current;
	}, []);

	const resetSessionToken = useCallback(() => {
		sessionTokenRef.current = null;
	}, []);

	const clearSuggestions = useCallback(() => {
		if (suggestAbortRef.current) suggestAbortRef.current.abort();
		if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
		setAddressSuggestions([]);
		setIsSuggestLoading(false);
	}, []);

	const closeDropdown = useCallback(
		(searchOverride) => {
			setShowDropdown(false);
			clearSuggestions();

			if (searchOverride !== undefined) {
				syncSearchFieldValue(searchOverride);
				return;
			}

			if (selectedAddressId === PENDING_ID && pendingAddress?.address) {
				syncSearchFieldValue(pendingAddress.address);
			} else {
				syncSearchFieldValue(selectedAddress ? formatAddressLabel(selectedAddress) : '');
			}
		},
		[clearSuggestions, pendingAddress, selectedAddress, selectedAddressId, syncSearchFieldValue]
	);

	const focusSearchInput = useCallback(() => {
		dropdownRef.current?.querySelector('.selector-field input')?.focus();
	}, []);

	const openAddressFormPopup = useCallback(
		(prefill = {}) => {
			onBeforeAddressFormOpen?.();
			closeDropdown();
			syncSearchFieldValue('');

			openPopup(
				<AddressForm
					title='account.address_management.addNewAddress'
					data={prefill}
					type={hasAddressType ? 'shipping' : undefined}
				/>,
				{ className: 'popup-address-form-block' }
			);
		},
		[closeDropdown, hasAddressType, onBeforeAddressFormOpen, openPopup, syncSearchFieldValue]
	);

	const fetchSuggestions = useCallback(
		async (text) => {
			if (!text.trim()) {
				clearSuggestions();
				return;
			}

			if (suggestAbortRef.current) suggestAbortRef.current.abort();
			const controller = new AbortController();
			suggestAbortRef.current = controller;
			setIsSuggestLoading(true);

			try {
				const results = await fetchAddressSuggestions({
					text,
					lang: selectedLangRef.current,
					bbox: DEFAULT_ARMENIA_BBOX,
					sessionToken: getSessionToken(),
					signal: controller.signal,
				});
				setAddressSuggestions(results);
			} catch (err) {
				if (err.name !== 'AbortError') setAddressSuggestions([]);
			} finally {
				setIsSuggestLoading(false);
			}
		},
		[clearSuggestions, getSessionToken]
	);

	const handleBranchSelect = useCallback(
		(branch) => {
			setSelectedBranchId(branch.id);
			closeDropdown();
		},
		[closeDropdown, setSelectedBranchId]
	);

	const handleAddressSelect = useCallback(
		(address) => {
			setSelectedAddressId(address.id);
			closeDropdown(formatAddressLabel(address));
		},
		[closeDropdown, formatAddressLabel, setSelectedAddressId]
	);

	const handlePendingAddressSelect = useCallback(() => {
		if (!pendingAddress) return;
		setSelectedAddressId(PENDING_ID);
		closeDropdown(pendingAddress.address || '');
	}, [closeDropdown, pendingAddress, setSelectedAddressId]);

	const handleSuggestSelect = useCallback(
		async (suggestion) => {
			const prefill = await resolveSuggestionPrefill(suggestion);
			resetSessionToken();
			openAddressFormPopup(prefill);
		},
		[openAddressFormPopup, resetSessionToken]
	);

	const handleCurrentLocation = useCallback(
		(e) => {
			e?.stopPropagation?.();
			if (!navigator.geolocation) return;

			setIsLocating(true);
			closeDropdown();

			navigator.geolocation.getCurrentPosition(
				async ({ coords }) => {
					let prefill = {};
					try {
						prefill = await resolveGeocodePrefill(coords, selectedLang);
					} catch {
						// geocode failed — open form empty
					} finally {
						setIsLocating(false);
					}
					openAddressFormPopup(prefill);
				},
				() => setIsLocating(false),
				{ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
			);
		},
		[selectedLang, closeDropdown, openAddressFormPopup]
	);

	const handleSearchFocus = useCallback(() => {
		setShowDropdown(true);
		syncSearchFieldValue('');
	}, [syncSearchFieldValue]);

	const handleSearchChange = useCallback(
		(e) => {
			const value = e?.target?.value ?? '';
			setShowDropdown(true);

			if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

			if (!value.trim()) {
				clearSuggestions();
				return;
			}

			debounceTimeoutRef.current = setTimeout(() => fetchSuggestions(value), 300);
		},
		[clearSuggestions, fetchSuggestions]
	);

	const toggleDropdown = useCallback(() => {
		if (showDropdown) {
			closeDropdown();
			return;
		}
		syncSearchFieldValue('');
		setShowDropdown(true);
		focusSearchInput();
	}, [showDropdown, closeDropdown, syncSearchFieldValue, focusSearchInput]);

	const takeawayPlaceholder = useMemo(
		() =>
			selectedBranch
				? normalizeBranchText(selectedBranch.name)
				: translate('deliveryTemplate.selectShopLocation'),
		[selectedBranch, translate]
	);

	useEffect(() => {
		if (!showDropdown) return;
		const handleClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				closeDropdown();
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [showDropdown, closeDropdown]);

	useEffect(() => () => clearSuggestions(), [clearSuggestions]);

	useEffect(() => {
		if (showDropdown || isSearching) return;
		if (selectedAddressId === PENDING_ID && pendingAddress?.address) {
			syncSearchFieldValue(pendingAddress.address);
		} else if (selectedAddress) {
			syncSearchFieldValue(formatAddressLabel(selectedAddress));
		} else {
			syncSearchFieldValue('');
		}
	}, [selectedAddress, selectedAddressId, showDropdown, isSearching, syncSearchFieldValue, pendingAddress]);

	return {
		branches,
		dropdownRef,
		showDropdown,
		isLocating,
		isSuggestLoading,
		isSearching,
		addressList,
		addressLoading,
		addressSuggestions,
		takeawayPlaceholder,
		handleBranchSelect,
		handleAddressSelect,
		handlePendingAddressSelect,
		handleSuggestSelect,
		handleCurrentLocation,
		handleSearchFocus,
		handleSearchChange,
		toggleDropdown,
		closeDropdown,
		formatBranchHours,
		normalizeBranchText,
		formatAddressLabel,
	};
};
